import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

// Enhanced mock articles data with full content
const mockArticlesWithContent = [
  {
    id: 1,
    title: "Understanding Anxiety: Causes and Coping Mechanisms",
    category: "Mental Health",
    tags: ["anxiety", "stress", "coping"],    
    readTime: "8 min read",
    hasQuiz: true,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 95,
    image: "https://images.pexels.com/photos/897817/pexels-photo-897817.jpeg?auto=compress&cs=tinysrgb&w=1200",
    excerpt: "Learn about the root causes of anxiety and discover effective techniques to manage anxious thoughts and feelings in daily life.",
    author: "Dr. Sarah Johnson",
    publishDate: "2025-05-15",
    content: [
      {
        type: "paragraph",
        text: "Anxiety is a natural response to stress, but when it becomes overwhelming, it can interfere with daily life. Understanding the root causes of anxiety is the first step toward managing it effectively."
      },
      {
        type: "heading",
        text: "What Causes Anxiety?"
      },
      {
        type: "paragraph",
        text: "Anxiety can stem from various sources, including genetic predisposition, brain chemistry, personality, and life events. Traumatic experiences, significant life changes, and chronic stress can all trigger anxiety disorders."
      },
      {
        type: "paragraph",
        text: "Research suggests that anxiety disorders may run in families, indicating a genetic component. Additionally, imbalances in neurotransmitters like serotonin and dopamine can contribute to anxiety symptoms."
      },
      {
        type: "heading",
        text: "Recognizing Anxiety Symptoms"
      },
      {
        type: "paragraph",
        text: "Anxiety manifests differently for everyone, but common symptoms include:"
      },
      {
        type: "list",
        items: [
          "Persistent worry or fear",
          "Restlessness or feeling on edge",
          "Rapid heartbeat and breathing",
          "Difficulty concentrating",
          "Sleep disturbances",
          "Muscle tension"
        ]
      },
      {
        type: "heading",
        text: "Effective Coping Strategies"
      },
      {
        type: "paragraph",
        text: "While anxiety can feel overwhelming, there are numerous evidence-based techniques to manage it:"
      },
      {
        type: "subheading",
        text: "1. Deep Breathing Exercises"
      },
      {
        type: "paragraph",
        text: "Controlled breathing helps activate your parasympathetic nervous system, which reduces the stress response. Try the 4-7-8 technique: inhale for 4 seconds, hold for 7 seconds, and exhale for 8 seconds."
      },
      {
        type: "subheading",
        text: "2. Progressive Muscle Relaxation"
      },
      {
        type: "paragraph",
        text: "This technique involves tensing and then releasing each muscle group in your body, promoting physical relaxation that can help ease anxiety."
      },
      {
        type: "subheading",
        text: "3. Cognitive Restructuring"
      },
      {
        type: "paragraph",
        text: "Challenge negative thought patterns by identifying cognitive distortions and replacing them with more balanced perspectives."
      },
      {
        type: "heading",
        text: "When to Seek Professional Help"
      },
      {
        type: "paragraph",
        text: "If anxiety significantly impacts your daily functioning, relationships, or quality of life, consider consulting a mental health professional. Therapy approaches like Cognitive Behavioral Therapy (CBT) have shown high effectiveness for anxiety disorders."
      },
      {
        type: "paragraph",
        text: "Remember that seeking help is a sign of strength, not weakness. With proper support and techniques, anxiety can be managed effectively."
      }
    ],
    relatedArticles: [3, 6]
  },
  {
    id: 2,
    title: "Building Emotional Resilience in Challenging Times",
    category: "Emotional Resilience",
    tags: ["resilience", "emotions", "growth"],
    readTime: "12 min read",
    hasQuiz: false,
    hasExercises: true,
    bookmarked: true,
    downloaded: true,
    relevanceScore: 87,
    image: "https://images.pexels.com/photos/897817/pexels-photo-897817.jpeg?auto=compress&cs=tinysrgb&w=1200",
    excerpt: "Develop stronger emotional resilience with practical strategies that help you bounce back from setbacks and grow through challenges.",
    author: "Dr. Michael Chen",
    publishDate: "2025-04-22",
    content: [
      {
        type: "paragraph",
        text: "Emotional resilience is the ability to adapt to stressful situations and recover from difficulties. It's not about avoiding challenges but developing the strength to navigate through them effectively."
      },
      {
        type: "heading",
        text: "The Foundations of Emotional Resilience"
      },
      {
        type: "paragraph",
        text: "Resilience isn't something we're born with—it's developed through experiences and intentional practice. The core components include self-awareness, emotional regulation, optimistic thinking, and strong social connections."
      },
      {
        type: "heading",
        text: "Practical Strategies to Build Resilience"
      },
      {
        type: "subheading",
        text: "1. Cultivate Self-Awareness"
      },
      {
        type: "paragraph",
        text: "Understanding your emotional responses helps you recognize when you need support or self-care. Regular reflection through journaling or mindfulness practices can enhance self-awareness."
      },
      {
        type: "subheading",
        text: "2. Develop Emotional Regulation Skills"
      },
      {
        type: "paragraph",
        text: "Learning to manage intense emotions prevents them from overwhelming you. Techniques like deep breathing, grounding exercises, and the 90-second rule (acknowledging that the chemical surge of emotion typically lasts about 90 seconds) can help you navigate emotional waves."
      },
      {
        type: "subheading",
        text: "3. Practice Cognitive Flexibility"
      },
      {
        type: "paragraph",
        text: "Resilient individuals can reframe challenges as opportunities for growth. When facing difficulties, ask yourself: 'What can I learn from this?' and 'How might this help me grow?'"
      },
      {
        type: "heading",
        text: "Resilience in Daily Practice"
      },
      {
        type: "paragraph",
        text: "Building resilience is an ongoing process that requires consistent effort. Here are some daily practices to strengthen your emotional resilience:"
      },
      {
        type: "list",
        items: [
          "Express gratitude for three specific things each day",
          "Engage in regular physical activity",
          "Maintain meaningful social connections",
          "Practice self-compassion when facing setbacks",
          "Set realistic goals and celebrate small wins"
        ]
      },
      {
        type: "paragraph",
        text: "Remember that building resilience takes time. Be patient with yourself as you develop these skills, and recognize that setbacks are part of the growth process."
      }
    ],
    relatedArticles: [5, 6]
  },
  // Add a third article for testing
  {
    id: 3,
    title: "Mindfulness Meditation for Beginners",
    category: "Mindfulness",
    tags: ["meditation", "mindfulness", "stress-relief"],
    readTime: "10 min read",
    hasQuiz: true,
    hasExercises: true,
    bookmarked: false,
    downloaded: false,
    relevanceScore: 90,
    image: "https://images.pexels.com/photos/3822622/pexels-photo-3822622.jpeg?auto=compress&cs=tinysrgb&w=1200",
    excerpt: "Learn the basics of mindfulness meditation and how it can help reduce stress and improve mental clarity.",
    author: "Emma Wilson",
    publishDate: "2025-03-10",
    content: [
      {
        type: "paragraph",
        text: "Mindfulness meditation is a simple practice that can help you become more present and aware in your daily life. It involves focusing your attention on the present moment and accepting it without judgment."
      },
      {
        type: "heading",
        text: "Benefits of Mindfulness Meditation"
      },
      {
        type: "paragraph",
        text: "Regular mindfulness practice has been shown to reduce stress, improve focus, and enhance emotional regulation. It can also help with anxiety, depression, and sleep problems."
      },
      {
        type: "heading",
        text: "Getting Started with Mindfulness"
      },
      {
        type: "paragraph",
        text: "Begin with just 5 minutes a day. Find a quiet place to sit comfortably, focus on your breath, and gently bring your attention back whenever your mind wanders."
      }
    ],
    relatedArticles: [1, 2]
  }
];

const ArticleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
 
  useEffect(() => {
    // Find the article by ID
    const articleId = parseInt(id || '0');
    console.log("Looking for article with ID:", articleId);
    const foundArticle = mockArticlesWithContent.find(a => a.id === articleId);
    
    if (foundArticle) {
      console.log("Found article:", foundArticle.title);
      setArticle(foundArticle);
    } else {
      console.log("Article not found. Available articles:", mockArticlesWithContent.map(a => a.id));
      // If article not found, set a default one for testing
      setArticle(mockArticlesWithContent[0]);
    }
  }, [id]);

  // Remove the handleBookmark and handleDownload functions as we're using the context now

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, fontFamily: '"Lato", sans-serif' }}>Loading article...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/self-help')}
        sx={{ mb: 2, color: '#4A306D' }}
      >
        Back to Articles
      </Button>

      {/* Article header */}
      <Box
        sx={{
          height: 300,
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${article.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: 4,
          mb: 3,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Box sx={{ p: 4, width: '100%' }}>
          <Chip
            label={article.category}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: '#4A306D',
              fontWeight: 500,
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              mb: 1, fontFamily: '"Lato", sans-serif'
            }}
          >
            {article.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 , fontFamily: '"Quicksand", sans-serif' }}>
              By {article.author}
            </Typography>
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 , fontFamily: '"Quicksand", sans-serif' }}>
              {article.readTime}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 , fontFamily: '"Quicksand", sans-serif' }}>
              {new Date(article.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Article actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 4,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(108, 141, 250, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {article.tags.map((tag: string) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ))}
        </Box>
       
      </Box>

      {/* Article content */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          mb: 4,
        }}
      >
        {article.content.map((section: any, index: number) => {
          switch (section.type) {
            case 'paragraph':
              return (
                <Typography key={index} paragraph sx={{ mb: 2, fontFamily: '"Quicksand", sans-serif' }}>
                  {section.text}
                </Typography>
              );
            case 'heading':
              return (
                <Typography
                  key={index}
                  variant="h5"
                  sx={{ fontWeight: 600, mt: 4, mb: 2, color: '#4A306D', fontFamily: '"Lato", sans-serif' }}
                >
                  {section.text}
                </Typography>
              );
            case 'subheading':
              return (
                <Typography
                  key={index}
                  variant="h6"
                  sx={{ fontWeight: 500, mt: 3, mb: 1, color: '#6C8DFA' }}
                >
                  {section.text}
                </Typography>
              );
            case 'list':
              return (
                <Box key={index} sx={{ mb: 3, ml: 2 }}>
                  {section.items.map((item: string, i: number) => (
                    <Box key={i} sx={{ display: 'flex', mb: 1 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#6C8DFA',
                          mt: 1,
                          mr: 2,
                        }}
                      />
                      <Typography sx={{ fontFamily: '"Quicksand", sans-serif' }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
              );
            default:
              return null;
          }
        })}

        {/* Author info */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', sx: { fontFamily: '"Quicksand", sans-serif' } }}>
          <Avatar
            sx={{ bgcolor: '#4A306D', width: 50, height: 50, mr: 2 }}
          >
            {article.author.split(' ').map((n: string) => n[0]).join('')}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 , fontFamily: '"Quicksand", sans-serif' }}>
              {article.author}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Quicksand", sans-serif' }}>
              Mental Health Specialist
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Interactive elements */}
      {(article.hasQuiz || article.hasExercises) && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            mb: 4,
            bgcolor: 'rgba(108, 141, 250, 0.05)',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, mb: 3, color: '#4A306D', fontFamily: '"Quicksand", sans-serif' }}
          >
            Interactive Elements
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {article.hasQuiz && (
              <Button
                variant="outlined"
                startIcon={<QuizIcon />}
                sx={{
                  flex: 1,
                  p: 2,
                  borderColor: '#6C8DFA',
                  color: '#6C8DFA',
                  borderRadius: 2,
                  fontFamily: '"Quicksand", sans-serif',
                  '&:hover': { borderColor: '#4A306D', color: '#4A306D' },
                }}
              >
                Take the Quiz
              </Button>
            )}

            {article.hasExercises && (
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                sx={{
                  flex: 1,
                  p: 2,
                  borderColor: '#FF9F7E',
                  color: '#FF9F7E',
                  borderRadius: 2,
                  fontFamily: '"Quicksand", sans-serif',
                  '&:hover': { borderColor: '#E58E6E', color: '#E58E6E' },
                }}
              >
                Try the Exercises
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Related articles */}
      {article.relatedArticles && article.relatedArticles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, mb: 3, color: '#4A306D', fontFamily: '"Quicksand", sans-serif' }}
          >
            Related Articles
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
            {article.relatedArticles.map((relatedId: number) => {
              const relatedArticle = mockArticlesWithContent.find(a => a.id === relatedId);
              if (!relatedArticle) return null;
              
              return (
                <Paper
                  key={relatedId}
                  elevation={0}
                  sx={{
                    width: 280,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    flexShrink: 0,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    },
                  }}
                  onClick={() => navigate(`/self-help/article/${relatedId}`)}
                >
                  <Box
                    sx={{
                      height: 140,
                      backgroundImage: `url(${relatedArticle.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, mb: 1, lineHeight: 1.3 }}
                    >
                      {relatedArticle.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1,
                      }}
                    >
                      {relatedArticle.excerpt}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ color: '#4A306D', fontWeight: 500, p: 0 }}
                    >
                      Read More
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Back to top button */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            background: 'linear-gradient(135deg, #6C8DFA 0%, #FF9F7E 100%)',
            color: 'white',
            borderRadius: 2,
            px: 4,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #5A7DE0 0%, #E58E6E 100%)',
            },
          }}
        >
          Back to Top
        </Button>
      </Box>
    </Container>
  );
};

export default ArticleDetail;